import { useContext, useEffect, useState } from 'react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { ConfigFeatureCheck } from '../../../util/ConfigFeatureCheck';
import { DevHubButton, DevHubDynamicIcon } from '@devxdiscover/devhub-ui';
import { Application } from '../types/InsightsTypes';
import { Feature } from '@devxdiscover/devhub-common';
import { UserContext } from '../../../contexts/UserContext';

interface FeedbackConfig {
  applicationId: number;
  title: string;
  login: string;
  apiURL?: string;
}

export type DevhubFeedbackProps = {
  applicationId: number;
  name: string;
};

declare class FaasFeedback {
  constructor(config: FeedbackConfig);

  showDialog(): void;
}

/**
 * Provides a feedback button for applications.
 * @param props - The properties passed to the ApplicationFeedbackButton.
 * @param props.application - The application to provide feedback for.
 * @param props.inHeader - Whether the button is displayed in a header (changes text color to white).
 */
export const ApplicationFeedbackButton = (props: {
  application: Application | DevhubFeedbackProps;
  inHeader?: boolean;
}) => {
  const { login } = useContext(UserContext);
  const applicationId = props.application?.applicationId;
  const applicationName = props.application?.name ?? '';
  const [feedback, setFeedback] = useState<FaasFeedback | undefined>();
  const config = useApi(configApiRef);
  const handleClick = () => {
    if (feedback) {
      feedback.showDialog();
    }
  };
  const feedbackUrl = config.getString('app.feedbackBaseUrl');

  useEffect(() => {
    // Dynamically load a script
    const script = document.createElement('script');
    script.src = new URL('js/feedback.min.js', feedbackUrl).toString();
    script.async = true;
    script.onload = () => {
      // Script has loaded, now we can use it
      const feedbackInstance = new FaasFeedback({
        applicationId: Number(applicationId),
        title: `Feedback for ${applicationName}`,
        login,
        apiURL: new URL(feedbackUrl).origin,
      });
      setFeedback(feedbackInstance);
    };
    document.head.appendChild(script);

    // Dynamically load a CSS file
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = new URL('css/feedback.css', feedbackUrl).toString();
    document.head.appendChild(link);

    // Clean up on component unmount
    return () => {
      document.head.removeChild(script);
      document.head.removeChild(link);
    };
  }, [applicationId, applicationName, feedbackUrl, login]);

  return (
    <ConfigFeatureCheck feature={Feature.FEEDBACK}>
      <DevHubButton
        textColor={props.inHeader ? 'white' : 'primary'}
        onClick={handleClick}
      >
        <DevHubDynamicIcon iconName="RateReview" />
        &nbsp;Give Feedback
      </DevHubButton>
    </ConfigFeatureCheck>
  );
};
